
import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';

import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { BackfillRequestApiModel } from '@api/mediaSubscriptions/models/backfill-request.api.model';
import { MediaSubscriptionMediaTypeEnum } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';

import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';

import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { QuotaInformation } from '@api/mediaSubscriptions/models/quota-information.api.model';

@Component({
    selector: 'agpr-social-topic-replay-dialog',
    templateUrl: './social-topic-replay-dialog.component.html',
    styleUrls: ['./social-topic-replay-dialog.component.scss'],
    standalone: false
})
export class SocialTopicReplayDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    messageSeverity = messageSeverityEnum;

    @Input() subscription: MediaSubscription;
    @Input() backfillBeginDate: string;
    @Input() backfillEndDate: string;
    @Input() ignoreCustomSub: boolean;
    @Input() quotaInfo: QuotaInformation;

    loading: boolean;
    estimateToLarge: boolean = false;
    estimateFailed: boolean = false;

    estimate: number;

    constructor(
        public coreDialogService: CoreDialogService,
        public mediaSubscriptionApiService: MediaSubscriptionApiService,
        public messageService: MessageService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, 'Backfill');
    }

    ngOnInit(): void {
        this.estimateBackfillSize();
    }

    getBackfillQuery(): BackfillRequestApiModel {
        return {
            backfillBeginDate: this.backfillBeginDate,
            backfillEndDate: this.backfillEndDate,
            mediaTypes: [MediaSubscriptionMediaTypeEnum.twitter],
            customSub: this.ignoreCustomSub ? null : this.subscription.toApiModel()
        };
    }

    estimateBackfillSize(): void {
        this.estimate = null;
        this.loading = true;
        this.estimateFailed = false;
        this.mediaSubscriptionApiService.estimateBackfillCount(this.subscription.id, this.getBackfillQuery()).subscribe({
            next: (count) => {
                this.estimate = count;
                this.loading = false;
                this.estimateToLarge = (this.estimate + (this.quotaInfo?.currentCount || 0)) >
                    ((this.quotaInfo?.maxCount + this.quotaInfo?.additionalCount) || 0);
            },
            error: () => {
                this.estimateFailed = true;
                this.loading = false;
                this.estimate = 0;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Social.agentWidget.replay.estimate.error.heading',
                    detail: 'Social.agentWidget.replay.estimate.error.message',
                });
            }
        });
    }

    /**
     * Action when the user clicks the "OK" button
     */
    ok(): void {
        if (!this.loading && !this.estimateToLarge) {
            this.mediaSubscriptionApiService.requestBackfill(
                this.subscription.id,
                this.getBackfillQuery()
            ).subscribe({
                next: () => {
                    this.closeDialog(true);
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Social.agentWidget.replay.request.success.heading',
                        detail: this.localizationService.transform(
                            'Social.agentWidget.replay.request.success.message',
                            { name: this.subscription.name }
                        )
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.agentWidget.replay.request.error.heading',
                        detail: this.localizationService.transform(
                            'Social.agentWidget.replay.request.error.message',
                            { name: this.subscription.name }
                        )
                    });
                }
            });
        }
    }

    ngOnDestroy(): void {
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog(false);
    }
}
